<template>
  <div>
    <header-admin :active-section="10" />
    <div class="text-right my-2">
      <button
        class="btn btn-primary"
        data-test="platform-message-create"
        @click="createNewMessage"
      >
        {{ trans("admin.platformMessages.createNewMessage") }}
      </button>
    </div>

    <div v-if="!showForm" class="panel panel-default panel-fill">
      <div class="panel-heading">
        <h3 class="panel-title">{{ trans("admin.platformMessages.title") }}</h3>
      </div>
      <div class="panel-body">
        <div class="table-responsive">
          <clip-loader v-show="loading" />

          <table class="table table-hover interactive-table">
            <thead class="sortable-table-header">
              <tr>
                <th>{{ trans("admin.platformMessages.status") }}</th>
                <th>{{ trans("admin.platformMessages.period") }}</th>
                <th>{{ trans("admin.platformMessages.stage") }}</th>
                <th>{{ trans("admin.platformMessages.interface") }}</th>
                <th>{{ trans("admin.platformMessages.type") }}</th>
                <th>{{ trans("admin.platformMessages.translations") }}</th>
                <th>{{ trans("admin.platformMessages.actions") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="message in messages"
                :key="message.id"
                :data-test="`platform-message-row-${message.id}`"
                :data-status="message.status"
                :data-stage="message.stage"
                :data-interface="message.interface"
                :data-type="message.type"
              >
                <td>
                  {{ trans("admin.platformMessages.status." + message.status) }}
                </td>
                <td>{{ message.start_date }} - {{ message.end_date }}</td>
                <td>{{ message.stage }}</td>
                <td>{{ message.interface }}</td>
                <td>{{ message.type }}</td>
                <td>
                  <span
                    v-for="(translation, index) in message.translations"
                    :key="translation.id"
                    :title="translation.subject + ' - ' + translation.message"
                    ><strong
                      >{{ translation.locale
                      }}{{
                        message.translations.length === index + 1 ? "" : ", "
                      }}
                    </strong>
                  </span>
                </td>
                <td>
                  <button
                    class="btn btn-primary mr-4"
                    type="button"
                    :data-test="`platform-message-edit-${message.id}`"
                    @click="editMessage(message)"
                  >
                    {{ trans("general.edit") }}
                  </button>
                  <button
                    class="btn btn-danger mr-4"
                    type="button"
                    :data-test="`platform-message-delete-${message.id}`"
                    @click="
                      confirmNegativeAction(
                        trans('admin.platformMessages.removePlatformMessage'),
                        () => deleteMessage(message.id),
                      )
                    "
                  >
                    {{ trans("general.formDeleteLabel") }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-if="showForm" class="panel panel-default panel-fill">
      <div class="panel-heading">
        <h3 class="panel-title">{{ formTitle }}</h3>
      </div>
      <div class="panel-body">
        <form @submit.prevent="saveMessage">
          <div class="container-fluid row md:tw-gap-8">
            <div class="form-group mb-4 select-custom-width">
              <div class="mb-3">
                <div
                  class="col d-flex align-items-center justify-content-between pr-0"
                >
                  <label class="mr-2 mb-0 tw-w-24 md:tw-w-32">
                    {{ trans("admin.platformMessages.stage") }}:
                  </label>
                  <v-select
                    v-model="form.stage"
                    :options="formData.stages"
                    required
                    class="w-100"
                    input-id="platform-message-form-stage"
                  />
                </div>
                <div class="text-right mt-1">
                  <validation-error
                    v-if="formErrors?.stage?.length"
                    :errors="formErrors.stage"
                  />
                </div>
              </div>
              <div class="mb-3">
                <div
                  class="col d-flex align-items-center justify-content-between pr-0"
                >
                  <label class="input-label mr-2 mb-0 tw-w-24 md:tw-w-32">
                    {{ trans("admin.platformMessages.interface") }}:
                  </label>
                  <v-select
                    v-model="form.interface"
                    :options="formData.interfaces"
                    required
                    class="w-100"
                    input-id="platform-message-form-interface"
                  />
                </div>
                <div class="text-right mt-1">
                  <validation-error
                    v-if="formErrors?.interface?.length"
                    :errors="formErrors.interface"
                  />
                </div>
              </div>
              <div class="mb3">
                <div
                  class="col d-flex align-items-center justify-content-between pr-0"
                >
                  <label class="input-label mr-2 mb-0 tw-w-24 md:tw-w-32">
                    {{ trans("admin.platformMessages.type") }}:
                  </label>
                  <v-select
                    v-model="form.type"
                    :options="formData.types"
                    required
                    class="w-100"
                    input-id="platform-message-form-type"
                  />
                </div>
                <div class="text-right mt-1">
                  <validation-error
                    v-if="formErrors?.type?.length"
                    :errors="formErrors.type"
                  />
                </div>
              </div>
            </div>
            <div class="form-group mb-2 date-input-custom-width">
              <div class="mb-3">
                <div
                  class="col d-flex align-items-center justify-content-between flex-column flex-md-row pr-0"
                >
                  <label
                    class="input-label mr-2 mb-0 align-self-start align-self-md-auto md:tw-w-32"
                  >
                    {{ trans("admin.platformMessages.startDate") }}:
                  </label>
                  <input
                    v-model="form.start_date"
                    type="datetime-local"
                    required
                    class="form-control min-w-0"
                    data-test="platform-message-form-start-date"
                  />
                </div>
                <div class="text-right mt-1">
                  <validation-error
                    v-if="formErrors?.start_date"
                    :errors="formErrors.start_date"
                  />
                </div>
              </div>
              <div class="mb-3">
                <div
                  class="col d-flex align-items-center justify-content-between flex-column flex-md-row pr-0"
                >
                  <label
                    class="input-label mr-2 mb-0 align-self-start align-self-md-auto md:tw-w-32"
                  >
                    {{ trans("admin.platformMessages.endDate") }}:
                  </label>
                  <input
                    v-model="form.end_date"
                    type="datetime-local"
                    required
                    class="form-control w-100"
                    data-test="platform-message-form-end-date"
                  />
                </div>
                <div class="text-right mt-1">
                  <validation-error
                    v-if="formErrors?.end_date"
                    :errors="formErrors.end_date"
                  />
                </div>
              </div>
            </div>
          </div>
          <button
            class="btn btn-primary m-2 mb-4"
            type="button"
            data-test="platform-message-add-translation"
            @click="addTranslation"
          >
            {{ trans("admin.platformMessages.addTranslation") }}
          </button>
          <div class="container-fluid mb-4">
            <div class="row mw-xl">
              <div
                v-for="(translation, i) in form.translations"
                class="card col-lg-5 col-md-12 p-0 m-3"
              >
                <div class="card-header">
                  <div class="d-flex align-items-center">
                    <label class="input-label mr-2 mb-0">
                      {{ trans("users.languageLabel") }}:
                    </label>
                    <v-select
                      v-model="translation.locale"
                      :options="formData.locales"
                      required
                      class="language-dropdown-width"
                      :input-id="`platform-message-form-translation-locale-${i}`"
                    />
                  </div>
                  <div class="mt-1">
                    <validation-error
                      v-if="formErrors?.translations?.[i]?.locale?.length"
                      :errors="formErrors.translations[i].locale"
                    />
                  </div>
                </div>
                <div class="card-body">
                  <div class="d-flex mb-4 flex-column">
                    <div class="d-flex flex-column align-items-center">
                      <label class="input-label align-self-start">
                        {{ trans("admin.platformMessages.subject") }}:
                      </label>
                      <input
                        v-model="translation.subject"
                        required
                        class="form-control"
                        :data-test="`platform-message-form-translation-subject-${i}`"
                      />
                    </div>
                    <div class="mt-1">
                      <validation-error
                        v-if="formErrors?.translations?.[i]?.subject?.length"
                        :errors="formErrors.translations[i].subject"
                      />
                    </div>
                  </div>
                  <div>
                    <label class="input-label mr-4">
                      {{ trans("admin.platformMessages.message") }}:
                    </label>
                    <textarea
                      v-model="translation.message"
                      required
                      class="form-control"
                      :data-test="`platform-message-form-translation-message-${i}`"
                    ></textarea>
                    <div class="mt-1">
                      <validation-error
                        v-if="formErrors?.translations?.[i]?.message?.length"
                        :errors="formErrors.translations[i].message"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            class="btn btn-primary mr-4"
            type="submit"
            data-test="platform-message-form-save"
          >
            {{ trans("general.formSaveLabel") }}
          </button>
          <button
            class="btn btn-danger mr-4"
            type="reset"
            @click="
              confirmNegativeAction(
                trans('admin.platformMessages.cancelCreateNewMessage'),
                cancelForm,
              )
            "
          >
            {{ trans("general.modalCancelButton") }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import HeaderAdmin from "./Header.vue";
import { practitionerHttpService } from "../../app/practitioner-portal/store/practitioner-http-service";
import DerSelect from "../Utils/DerSelect.vue";
import FormError from "../Utils/FormError.vue";
import ValidationError from "../Utils/ValidationError.vue";
import ClipLoader from "../Utils/ClipLoader.vue";

export default {
  components: {
    ClipLoader,
    ValidationError,
    FormError,
    DerSelect,
    HeaderAdmin,
  },
  data() {
    return {
      messages: [],
      formData: {
        stages: [],
      },
      formErrors: [],
      isLoading: false,
      width: 0,
      showForm: false,
      formTitle: "",
      form: {
        id: null,
        stage: "",
        interface: "",
        type: "",
        start_date: "",
        end_date: "",
        translations: [],
      },
      loading: false,
    };
  },

  mounted() {
    this.loadMessages();
  },
  methods: {
    loadMessages() {
      this.isLoading = true;
      practitionerHttpService
        .get("/api/internal/admin/platform-messages")
        .then((response) => {
          this.messages = response.data?.messages;
          this.formData = response.data?.formData;
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    createNewMessage() {
      this.clearForm();
      this.formTitle = this.trans("admin.platformMessages.createNewMessage");
      this.form = {
        id: null,
        stage: "",
        interface: "",
        type: "",
        start_date: "",
        end_date: "",
        translations: [],
      };
      this.showForm = true;
    },
    editMessage(message) {
      this.formTitle = this.trans("admin.platformMessages.editMessage");
      this.form = { ...message };
      this.showForm = true;
    },
    saveMessage() {
      this.formErrors.value = {};
      const url = this.form.id
        ? `/api/internal/admin/platform-messages/${this.form.id}`
        : "/api/internal/admin/platform-messages";
      const method = "post";
      practitionerHttpService[method](url, this.form)
        .then((response) => {
          this.loadMessages();
          this.showForm = false;
        })
        .catch((error) => {
          if (error?.response?.status === 422) {
            this.formErrors = error.response.data;
          } else {
            console.error(error);
          }
        });
    },
    deleteMessage(id) {
      practitionerHttpService
        .delete(`/api/internal/admin/platform-messages/${id}`)
        .then((response) => {
          this.loadMessages();
        })
        .catch((error) => {
          console.error(error);
        });
    },
    addTranslation() {
      this.form.translations.push({ locale: "", subject: "", message: "" });
    },
    confirmNegativeAction(action, onConfirm) {
      this.$vueAlert
        .confirm(
          this.trans("action.confirmAction.question", { action: action }),
          undefined,
          undefined,
          {
            confirmButtonText: this.trans("general.modalConfirmButton"),
            cancelButtonText: this.trans("general.modalCancelButton"),
            onOpen: (popup) => {
              popup
                .querySelector(".swal2-confirm")
                ?.setAttribute("data-test", "platform-message-confirm-button");
            },
          },
        )
        .then(() => {
          if (typeof onConfirm === "function") {
            onConfirm(); // Execute the passed function
          }
        });
    },
    cancelForm() {
      this.showForm = false;
      this.clearForm();
    },
    clearForm() {
      this.form.values = {};
      this.formErrors = [];
    },
  },
};
</script>

<style scoped>
label {
  white-space: nowrap;
}
.select-custom-width {
  width: 100%;
  max-width: 256px;
}
.date-input-custom-width {
  width: 100%;
  max-width: 320px;
  min-width: 300px;
}

.language-dropdown-width {
  width: 6rem;
  white-space: nowrap;
}

@media (max-width: 900px) {
  .select-custom-width {
    max-width: 100%;
  }
  .date-input-custom-width {
    max-width: 100%;
  }
}
</style>
